/**
 * Lightweight client-side name fuzzy match (trigram Dice coefficient).
 * Mirrors the server pg_trgm thresholds used by `/api/cards` for local
 * filters (collection, binders, deck browser) that never hit Postgres.
 */

export const FUZZY_NAME_MIN_LEN = 4;
export const FUZZY_NAME_SIMILARITY = 0.34;

function trigrams(s: string): string[] {
  const padded = `  ${s} `;
  const out: string[] = [];
  for (let i = 0; i < padded.length - 2; i += 1) {
    out.push(padded.slice(i, i + 3));
  }
  return out;
}

/** Dice coefficient over character trigrams — same family as pg_trgm `similarity`. */
export function trigramSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const ta = trigrams(a);
  const tb = trigrams(b);
  if (ta.length === 0 || tb.length === 0) return 0;
  const counts = new Map<string, number>();
  for (const t of ta) counts.set(t, (counts.get(t) ?? 0) + 1);
  let overlap = 0;
  for (const t of tb) {
    const n = counts.get(t) ?? 0;
    if (n > 0) {
      overlap += 1;
      counts.set(t, n - 1);
    }
  }
  return (2 * overlap) / (ta.length + tb.length);
}

/**
 * True when `query` starts a word in `name`. "lux" matches "Lux - Illuminated"
 * and does not match "deluxe".
 */
export function nameHasWordPrefix(name: string, query: string): boolean {
  const n = name.trim().toLowerCase();
  const q = query.trim().toLowerCase();
  if (!n || !q) return false;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?:^|[^a-z0-9])${escaped}`).test(n);
}

/**
 * True when `name` contains `query` as a word prefix, or (for longer queries)
 * is within the trigram similarity threshold — including space-slip typos
 * like "oceand rake" ≈ "ocean drake".
 */
export function fuzzyNameMatches(
  name: string,
  query: string,
  threshold = FUZZY_NAME_SIMILARITY,
): boolean {
  const n = name.trim().toLowerCase();
  const q = query.trim().toLowerCase();
  if (!n || !q) return false;
  if (nameHasWordPrefix(n, q)) return true;
  if (q.length < FUZZY_NAME_MIN_LEN) return false;
  if (trigramSimilarity(n, q) >= threshold) return true;
  // Space-insensitive pass: "oceand rake" and "ocean drake" collapse to the same string.
  const nCompact = n.replace(/\s+/g, '');
  const qCompact = q.replace(/\s+/g, '');
  if (qCompact.includes(nCompact)) return true;
  return trigramSimilarity(nCompact, qCompact) >= threshold;
}
